import { FacturationVenio } from './FacturationVenio.js';
import catalogue from './Interrupteurs.module.css';
import formStyles from '../styles/form.module.css';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Dialog, Input } from '@artosera/ui';
import {
  CATEGORIES_MODULE,
  ETATS_PUBLICATION_ADDON,
  type EtatPublicationAddon,
  type ModuleAddon,
} from '@artosera/shared';
import { Link } from 'react-router';
import { addonsConsoleApi, marketplaceConsoleApi } from '../lib/addons.js';
import { PrixAddon } from './PrixAddon.js';
import { useLibellesModules } from '../lib/modules.js';
import { usePeut } from '../auth/usePeut.js';
import styles from '../styles/page.module.css';

/** Le signal est une confirmation nommée, réservée à la direction. */
export function Addons(): JSX.Element {
  const { t, i18n } = useTranslation('console');
  const peut = usePeut();
  const libelles = useLibellesModules();
  const client = useQueryClient();
  const cle = ['console', 'addons'];
  const requete = useQuery({ queryKey: cle, queryFn: addonsConsoleApi.globale });
  const prix = useQuery({
    queryKey: ['console', 'addons', 'prix'],
    queryFn: marketplaceConsoleApi.prix,
    enabled: peut('console:read-addon-sales'),
  });
  const [cible, setCible] = useState<ModuleAddon | null>(null);
  const [etat, setEtat] = useState<EtatPublicationAddon>('essai');
  const [motif, setMotif] = useState('');
  const [confirme, setConfirme] = useState(false);
  const mutation = useMutation({
    mutationFn: () => addonsConsoleApi.publier(cible!, { etat, motif }),
    onSuccess: (donnees) => {
      client.setQueryData(cle, donnees);
      setCible(null);
      void client.invalidateQueries({ queryKey: ['console', 'galerie'] });
    },
  });
  const date = (valeur: string | null) =>
    valeur ? new Date(valeur).toLocaleString(i18n.language) : t('addons.aucun');
  return (
    <section className={catalogue.catalogue}>
      <h1>{t('addons.titre')}</h1>
      {peut('console:read-addon-sales') ? (
        <Link to="/addons/ventes">{t('ventes.titre')}</Link>
      ) : null}
      <FacturationVenio />
      {requete.isPending ? <p>{t('addons.charger')}</p> : null}
      {prix.isError ? <p role="alert">{t('addons.prix.erreur')}</p> : null}
      {requete.isError ? <p role="alert">{t('addons.erreur')}</p> : null}
      {CATEGORIES_MODULE.map((categorie) => {
        const lignes = requete.data?.addons.filter((a) => a.categorie === categorie) ?? [];
        if (!lignes.length) return null;
        return (
          <section className={`${styles.panneau} ${catalogue.catalogue}`} key={categorie}>
            <h2>{t(`interrupteurs.categories.${categorie}`)}</h2>
            {lignes.map((ligne) => (
              <article className={catalogue.module} key={ligne.addon}>
                <h3>{libelles.libelle(ligne.addon)}</h3>
                <p>{libelles.description(ligne.addon)}</p>
                {!ligne.disponible ? <Badge>{t('addons.bientot')}</Badge> : null}
                <Badge>{t(`addons.etats.${ligne.publication.etat}`)}</Badge>
                <p>
                  {ligne.publication.motif} · {ligne.publication.auteur} ·{' '}
                  {date(ligne.publication.modifieLe)}
                </p>
                <p>
                  {Object.entries(ligne.enCours)
                    .map(([mode, nombre]) => `${t(`addons.modes.${mode}`)} : ${nombre}`)
                    .join(' · ')}
                </p>
                {peut('console:publish-addon') ? (
                  <Button
                    onClick={() => {
                      mutation.reset();
                      setCible(ligne.addon);
                      setEtat(ligne.publication.etat);
                      setMotif('');
                      setConfirme(false);
                    }}
                  >
                    {t('addons.publication')}
                  </Button>
                ) : null}
                {peut('console:read-addon-sales') && prix.data ? (
                  <PrixAddon
                    addon={ligne.addon}
                    ligne={prix.data.prix.find((p) => p.addon === ligne.addon)}
                  />
                ) : null}
              </article>
            ))}
          </section>
        );
      })}
      <section className={styles.panneau}>
        <h2>{t('addons.echeances')}</h2>
        <ul>
          {requete.data?.echeances.map((droit) => (
            <li key={`${droit.galerieId}-${droit.addon}`}>
              <Link to={`/galeries/${droit.galerieId}/addons`}>{droit.galerie}</Link> ·{' '}
              {libelles.libelle(droit.addon)} · {t(`addons.modes.${droit.mode}`)} ·{' '}
              {date(droit.jusquAu)}
            </li>
          ))}
        </ul>
        {requete.data?.echeances.length === 0 ? <p>{t('addons.aucun')}</p> : null}
      </section>
      <section className={styles.panneau}>
        <h2>{t('addons.historique')}</h2>
        <ul>
          {requete.data?.historique.map((ligne, rang) => (
            <li key={rang}>
              {libelles.libelle(ligne.addon)} · {t(`addons.etats.${ligne.etat}`)} · {ligne.motif} ·{' '}
              {ligne.auteur} · {date(ligne.modifieLe)}
            </li>
          ))}
        </ul>
      </section>
      <Dialog
        closeLabel={t('addons.annuler')}
        open={cible !== null}
        onOpenChange={(ouvert) => {
          if (!ouvert && !mutation.isPending) setCible(null);
        }}
        title={cible ? libelles.libelle(cible) : t('addons.titre')}
      >
        <form
          className={formStyles.champ}
          onSubmit={(event) => {
            event.preventDefault();
            if (etat !== 'publiee' || confirme) mutation.mutate();
          }}
        >
          <label htmlFor="addon-publication">{t('addons.publication')}</label>
          <select
            id="addon-publication"
            value={etat}
            onChange={(e) => {
              setEtat(e.target.value as EtatPublicationAddon);
              setConfirme(false);
            }}
          >
            {ETATS_PUBLICATION_ADDON.map((e) => (
              <option
                key={e}
                value={e}
                disabled={
                  e === 'publiee' &&
                  !requete.data?.addons.find((a) => a.addon === cible)?.disponible
                }
              >
                {t(`addons.etats.${e}`)}
              </option>
            ))}
          </select>
          <label htmlFor="publication-motif">{t('addons.motif')}</label>
          <Input
            id="publication-motif"
            required
            maxLength={500}
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
          />
          {etat === 'publiee' && cible ? (
            <label>
              <input
                type="checkbox"
                checked={confirme}
                onChange={(e) => setConfirme(e.target.checked)}
              />
              {t('addons.confirmer', { addon: libelles.libelle(cible) })}
            </label>
          ) : null}
          {mutation.isError ? <p role="alert">{t('addons.echec')}</p> : null}
          <Button
            type="submit"
            disabled={mutation.isPending || !motif.trim() || (etat === 'publiee' && !confirme)}
          >
            {t('addons.enregistrer')}
          </Button>
          <Button type="button" disabled={mutation.isPending} onClick={() => setCible(null)}>
            {t('addons.annuler')}
          </Button>
        </form>
      </Dialog>
    </section>
  );
}
